// VideoInView.js
// Plays `<video data-autoplay>` only while it's on screen. A plain `autoplay`
// makes the browser download every video on the page at once, even ones in
// closed columns or far down a project; this way each one loads when it's
// first seen and pauses when it scrolls away.
// Videos added later (lazy-loaded columns, a project opened in the overlay)
// are picked up automatically.

export default class VideoInView {
  constructor(opts = {}) {
    this.selector = opts.selector || 'video[data-autoplay]';
    // Start a little before the video comes into view so it's already moving
    this.rootMargin = opts.rootMargin || '100px 0px';

    this._io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) target.play().catch(() => {});
        else target.pause();
      });
    }, { rootMargin: this.rootMargin });

    this._mo = new MutationObserver((records) => {
      records.forEach((r) => r.addedNodes.forEach((n) => {
        if (n.nodeType === 1) this.observe(n);
      }));
    });
  }

  start() {
    this.observe(document);
    this._mo.observe(document.body, { childList: true, subtree: true });
  }

  /** Watch every matching video in (or at) `root`. */
  observe(root) {
    if (root.matches?.(this.selector)) this._watch(root);
    root.querySelectorAll?.(this.selector).forEach((v) => this._watch(v));
  }

  _watch(video) {
    if (video._inView) return;
    video._inView = true;
    video.muted = true; // the attribute alone isn't always enough for play() after cloning
    this._io.observe(video);
  }
}
